/**
 * Negociação da versão do envelope entre os aparelhos de um espaço (ADR 0181). Módulo PURO.
 *
 * O problema: o envelope v2 (ADR 0177) é 20× menor, mas um app antigo não o lê — um segmento
 * v2 trava o sync dele até atualizar. A régua "só vira quando a base inteira atualizar" nunca
 * fecha. Então cada aparelho que SABE ler v2 deixa isso dito no Drive, num arquivo vazio cujo
 * NOME carrega a capacidade (`kontto_device_<deviceId>_v2_s45.json`): a listagem que o ciclo já
 * faz responde tudo, sem download. App antigo nunca publica nada — e é exatamente isso que o
 * protege: quem escreve usa a MENOR versão entre os aparelhos conhecidos, e aparelho sem
 * arquivo conta como "só lê v1".
 *
 * Aparelho CONHECIDO = qualquer um com segmento listado, com arquivo de capacidade, lembrado
 * localmente (`seen_<deviceId>`, atualizado ao ver segmento dele ou o nome dele no `basis` de um
 * checkpoint — a GC apaga segmentos, o basis não esquece ninguém) ou com cursor. Quem não dá
 * sinal há `DEVICE_STALE_MS` deixa de segurar os outros: se voltar em app antigo, lê "atualize o
 * Kontto" — o acordo com o Laysson em 12/10/2026 ("se um atualizar, o outro também tem").
 */

import type { EnvelopeVersion } from '@/lib/backupCrypto';

/** O maior envelope que ESTE build lê e escreve. Sobe junto com o leitor (ADR 0177). */
export const ENVELOPE_READ_MAX: EnvelopeVersion = 2;

/** Sem sinal por este tempo, o aparelho deixa de segurar a versão dos outros (45 dias). */
export const DEVICE_STALE_MS = 45 * 24 * 60 * 60 * 1000;

export const DEVICE_FILE_PREFIX = 'kontto_device_';
const DEVICE_FILE_RE = /^kontto_device_([^_]+)_v(\d+)_s(\d+)\.json$/;

export interface DeviceCapabilities {
    deviceId: string;
    /** maior versão de envelope que o aparelho lê */
    envelope: number;
    /** versão de schema (índice do journal Drizzle) que o aparelho aplica */
    schema: number;
}

/** `kontto_device_<deviceId>_v<envelope>_s<schema>.json` — a capacidade mora no nome. */
export function deviceFileName(caps: DeviceCapabilities): string {
    return `${DEVICE_FILE_PREFIX}${caps.deviceId}_v${caps.envelope}_s${caps.schema}.json`;
}

export function parseDeviceFileName(name: string): DeviceCapabilities | null {
    const m = DEVICE_FILE_RE.exec(name);
    if (!m) return null;
    const envelope = Number(m[2]);
    const schema = Number(m[3]);
    if (!Number.isInteger(envelope) || envelope < 1 || !Number.isInteger(schema)) return null;
    return { deviceId: m[1], envelope, schema };
}

/** Conteúdo do arquivo — só pra quem abrir a pasta no Drive entender; nenhum leitor depende. */
export function deviceFileContent(caps: DeviceCapabilities, nowIso: string): string {
    return JSON.stringify({ format: 'kontto-device', deviceId: caps.deviceId, reads: { envelope: caps.envelope, schema: caps.schema }, updatedAt: nowIso });
}

export interface KnownDevice {
    deviceId: string;
    /** último sinal de vida (ms desde a época) */
    lastSeenMs: number;
    /** maior envelope que ele declarou ler; null = nunca declarou (app antigo: só v1) */
    readsEnvelope: number | null;
}

/** Junta sinais do mesmo aparelho: o sinal mais recente e a maior capacidade declarada. */
export function noteDevice(known: Map<string, KnownDevice>, deviceId: string, lastSeenMs: number, readsEnvelope: number | null = null): void {
    const prev = known.get(deviceId);
    if (!prev) {
        known.set(deviceId, { deviceId, lastSeenMs, readsEnvelope });
        return;
    }
    prev.lastSeenMs = Math.max(prev.lastSeenMs, Number.isFinite(lastSeenMs) ? lastSeenMs : 0);
    if (readsEnvelope !== null && (prev.readsEnvelope === null || readsEnvelope > prev.readsEnvelope)) prev.readsEnvelope = readsEnvelope;
}

/**
 * A versão que ESTE aparelho deve escrever agora: a menor entre o que cada aparelho vivo do
 * espaço lê, limitada ao que este build lê. Sozinho no espaço, escreve o máximo.
 */
export function negotiateEnvelopeVersion(known: Iterable<KnownDevice>, selfId: string, nowMs: number): EnvelopeVersion {
    let version: number = ENVELOPE_READ_MAX;
    for (const d of known) {
        if (d.deviceId === selfId) continue;
        if (nowMs - d.lastSeenMs > DEVICE_STALE_MS) continue;
        const reads = d.readsEnvelope ?? 1;
        if (reads < version) version = reads;
    }
    return (version >= 2 ? 2 : 1) as EnvelopeVersion;
}
